import {
    Button,
    Widget,
    WidgetActions,
    WidgetContent,
    WidgetHeader,
    WidgetTitle,
} from "frontend-components";
import { Cycle } from "@dashboard/components/Cycle/Cycle";
import { Circle } from "@dashboard/components/Shapes/Circle";
import { useTranslation } from "react-i18next";
import { YearlyTasks } from "./YearlyTasks";
import { useYearlyCycle } from "@dashboard/hooks/useYearlyCycle";
import { YearlyCycleContext } from "./YearlyCycleContext";
import { useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Can } from "@dashboard/casl/taskAbility";
import { subject } from "@casl/ability";
import { useHseInstance } from "@dashboard/hooks/useHseInstance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faPlus } from "@fortawesome/sharp-regular-svg-icons";
import { indexToMonth } from "@dashboard/utils/months";
import { useSelectedCustomerIdParam } from "@dashboard/contexts/selectedCustomerContext";

export default function YearlyCycle() {
    const { t } = useTranslation();
    const { active, trigger, nodes } = useYearlyCycle();
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear);
    const { instanceId } = useHseInstance();
    const navigate = useNavigate();
    const customerId = useSelectedCustomerIdParam();
    const dashboardProfileParams = useParams({
        from: "/_hub/$customerId/dashboard/$profileId",
    });

    const openCreateModal = () => {
        void navigate({
            to: ".",
            search: (prev) => ({ ...prev, task: "new", edit: undefined }),
        });
    };

    return (
        <Widget>
            <WidgetHeader>
                <WidgetTitle>
                    <Link
                        to="/$customerId/dashboard/$profileId/annual-cycle"
                        params={{
                            customerId,
                            profileId: dashboardProfileParams.profileId,
                        }}
                        className="group inline-flex items-center gap-2 hover:underline"
                    >
                        {t("dashboard.yearlyCycleTitle", { year })}
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className="text-compendia-greige group-hover:text-compendia-foreground size-4 transition-all duration-300 ease-out group-hover:translate-x-1"
                        />
                    </Link>
                </WidgetTitle>
                <WidgetActions>
                    <Can I="create" this={subject("Task", { instanceId })}>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t("task.newActivity")}
                            onClick={openCreateModal}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                        </Button>
                    </Can>
                </WidgetActions>
            </WidgetHeader>
            <WidgetContent className="flex-1 flex-col items-stretch justify-between overflow-hidden overflow-y-auto lg:flex-row">
                <YearlyCycleContext.Provider value={{ year, active }}>
                    <div className="order-2 flex h-[50%] w-full shrink-0 flex-col overflow-hidden lg:order-1 lg:h-full lg:w-[30%]">
                        <YearlyTasks />
                    </div>
                    <div className="order-1 flex h-[50%] w-full min-w-0 items-center justify-center lg:order-2 lg:h-full lg:min-h-0 lg:w-[70%] min-h-[300px]">
                        <Cycle year={year} onYearChange={setYear}>
                            {Array.from({ length: nodes }).map((_, idx) => (
                                <Circle
                                    key={idx}
                                    mainProps={{ cursor: "pointer", className: "group" }}
                                    circleProps={{
                                        className: `${
                                            active.has(idx)
                                                ? "fill-chameleon-green group-hover:fill-chameleon-green/90"
                                                : "fill-compendia-greige-40 group-hover:fill-compendia-greige-60 stroke-compendia-greige stroke-1"
                                        } `,
                                    }}
                                    textProps={{ className: "fill-compendia-foreground" }}
                                    label={t(`common.month.short.${indexToMonth[idx + 1]}`)}
                                    onClick={() => trigger(idx)}
                                />
                            ))}
                        </Cycle>
                    </div>
                </YearlyCycleContext.Provider>
            </WidgetContent>
        </Widget>
    );
}
